import { useEffect, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { hasPendingSso, takePendingSso } from "plg/auth/ssoRedirect";
import type { SsoResult } from "plg/auth/ssoRedirect";
import type { PlgAuthConfig } from "plg/types";
import { PLG_SIGNUP_ROUTE } from "constants/routes";

/**
 * The PLG Microsoft app on the sign-in page. Sign-in leaves this tab for Microsoft (loginRedirect) and comes back
 * to /signup, where this picks up the id token, but only for a sign-in this tab started (its nonce is kept).
 * The Azure app must list `<origin>/signup` as a Single-page application redirect URI.
 */
export default function usePlgMsal(config: PlgAuthConfig | null): { msal: PublicClientApplication | null; result: SsoResult | null } {
  const clientId = config?.microsoft.clientId;
  const authority = config?.microsoft.authority;
  const [msal, setMsal] = useState<PublicClientApplication | null>(null);
  const [result, setResult] = useState<SsoResult | null>(null);

  useEffect(() => {
    if (!clientId) return undefined;
    let live = true;
    const instance = new PublicClientApplication({
      auth: { clientId, authority, redirectUri: `${window.location.origin}${PLG_SIGNUP_ROUTE}` },
    });
    instance
      .initialize()
      .then(() => instance.handleRedirectPromise({ navigateToLoginRequestUrl: false }))
      .then((res) => {
        if (!live) return;
        setMsal(instance);
        if (!res?.idToken || !hasPendingSso("microsoft")) return;
        const pending = takePendingSso();
        if (pending?.provider === "microsoft") setResult({ provider: "microsoft", idToken: res.idToken, nonce: pending.nonce });
      })
      .catch(() => {
        // A failed or cancelled return: the button stays, and the visitor can try again.
        if (live) setMsal(instance);
      });
    return () => {
      live = false;
    };
  }, [clientId, authority]);

  return { msal, result };
}
